import type { PromotedPriceApiResponse } from './types'

import type { FilterSelection } from '@/scopes/prices/domain/filters/types'

import defaultFetch from 'isomorphic-unfetch'
import { AbTests, OPTIMIZELY_TESTS_KEY, OPTIMIZELY_TESTS_KEYS } from '@/scopes/common/infrastructure/ab-testing/optimizely/types'

import { createBrowserQueryString } from '@/scopes/common/infrastructure/query-strings/browserQueryString'
import {
  QueryStringSerializer,
} from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import { BrowserQueryString } from '@/scopes/common/infrastructure/query-strings/types/browserQueryString'
import { PromotedPrice } from '@/scopes/prices/domain/promoted-price/types'
import filterQueryStringSerializer
  from '@/scopes/prices/infrastructure/filter-query-string-serializer/filterQueryStringSerializer'
import { pricesPassThroughParams } from '@/scopes/prices/types'
import { getPassThroughParams } from '@/scopes/search/search/domain/getPassThroughParams'
import { createPromotedPriceResponseMapper, type PromotedPriceResponseMapper } from './pormotedPriceMapper'

interface PromotedPriceContext {
  accoId: string
  contextItemId: string
  promotedPriceId: string
}

type PromotedPriceCriteria = PromotedPriceContext & { filterSelection: FilterSelection }
interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
  queryStringSerializer: QueryStringSerializer<PromotedPriceContext & FilterSelection>
  promotedPriceResponseMapper: PromotedPriceResponseMapper
  browserQuery: BrowserQueryString
  abTests: AbTests
}

export interface PromotedPriceApiClient {
  getPromotedPrice: (criteria: PromotedPriceCriteria) => Promise<PromotedPrice>
}

export function createPromotedPriceApiClient(dependencies: Partial<Dependencies> = {}) {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = filterQueryStringSerializer,
    promotedPriceResponseMapper = createPromotedPriceResponseMapper(),
    browserQuery = createBrowserQueryString(),
    abTests = {},
  } = dependencies as Dependencies

  const EnabledBTests = (Object.keys(abTests) as OPTIMIZELY_TESTS_KEY[])
    .filter(key => abTests[key])
    .map(key => OPTIMIZELY_TESTS_KEYS[key])
    .join(',')

  const fetchOptions: RequestInit = {
    referrerPolicy: 'no-referrer-when-downgrade',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(EnabledBTests && { EnabledBTests }),
    },
  }

  async function getPromotedPrice(criteria: PromotedPriceCriteria) {
    const url = buildAPIUrl(criteria)

    const response = await fetch(url, fetchOptions)

    if (response.status !== 200)
      return Promise.reject(new Error('error loading promoted price'))

    const data = (await response.json()) as PromotedPriceApiResponse

    return promotedPriceResponseMapper.map(data)
  }

  function buildAPIUrl({ accoId, contextItemId, promotedPriceId, filterSelection }: PromotedPriceCriteria) {
    const filtersContext = {
      accoId,
      contextItemId,
      promotedPriceId,
    }

    const queryParams = new URLSearchParams(browserQuery.get()!)
    const passThroughParams = getPassThroughParams(queryParams, pricesPassThroughParams)

    const queryString = queryStringSerializer.serialize({
      ...filtersContext,
      ...filterSelection,
      ...passThroughParams,
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/PromotedPrice/GetPromotedPriceApi?${queryString}`
  }

  return { getPromotedPrice }
}
